import { getParent, type Instance, types } from 'mobx-state-tree';

import type { ApplicationAttachmentV2 } from 'src/App/AppPageBlock/graphql/hirer-types.generated';
import type { fetchApplicationAttachmentsV2 } from 'src/graphql/fetchApplicationAttachmentsV2.hirer.graphql';
import { mapGraphAttachmentTypeToStore } from 'src/stores/JobApplicationStore/utils/mapGraphAttachmentTypeToStore';

import {
  HIRER_UPLOADED,
  isApplicationDocuments,
} from '../../constants/attachmentTypes';
import { ACTION_DONE, ACTION_ERROR, stateType } from '../state';

import Attachment, { type IAttachment } from './Attachment';
import type { IJobApplication } from './JobApplication';

const mapToAttachment = (
  attachment:
    | Awaited<ReturnType<typeof fetchApplicationAttachmentsV2>>[0]
    | ApplicationAttachmentV2,
) =>
  Attachment.create({
    id: attachment.blobId,
    type: mapGraphAttachmentTypeToStore(attachment.attachmentType),
    filename: attachment.fileName,
    uploadedDate: attachment.uploadedDate,
    uploadedUser: attachment.uploadedUser,
  });

const AttachmentsStore = types
  .model('Attachments', {
    attachmentList: types.array(Attachment),
    state: stateType,
  })
  .views((self) => ({
    get jobApplication(): IJobApplication {
      return getParent(self) as IJobApplication;
    },
  }))
  .views((self) => ({
    get hirerUploadedDocuments() {
      return self.attachmentList
        .filter(
          (attachment: IAttachment) => attachment.type === HIRER_UPLOADED.type,
        )
        .sort((attachment1: IAttachment, attachment2: IAttachment) =>
          (attachment2.uploadedDate || '').localeCompare(
            attachment1.uploadedDate || '',
          ),
        );
    },
    get applicationDocuments() {
      return self.attachmentList.filter((attachment: IAttachment) =>
        isApplicationDocuments(attachment.type),
      );
    },
  }))
  .actions((self) => {
    const setAttachmentList = ({
      data,
    }: {
      data: ApplicationAttachmentV2[];
    }): 'done' | 'error' => {
      try {
        self.attachmentList.replace(data.map((a) => mapToAttachment(a)));
        return ACTION_DONE;
      } catch (error) {
        return ACTION_ERROR;
      }
    };

    return {
      setAttachmentList,
    };
  });

export interface IAttachmentsStore extends Instance<typeof AttachmentsStore> {}

export default AttachmentsStore;
